import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { LlmColumnModel } from '../llm-column.model';
import { toast } from '@cotera/client/app/components/ui';
import { Assert } from '@cotera/utilities';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { DataGridModel } from '@cotera/client/v0/data-grid';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app/modal-manager';
import { Input } from '@cotera/client/v0/components/ui/input';
import { useState } from 'react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { AtcCellModel } from '@cotera/client/v0/data-grid/atc/cell.model';
import { DG } from '@cotera/sdk/core';
import { Ty } from '@cotera/era';
import { CoteraDuckDB, DuckDBQueryResult } from '@cotera/client/app/etc/duckdb';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { v4 } from 'uuid';

type Inputs = {
  rows?: number;
};

/** Synthetic column holding the computed LLM messages, queried alongside the row data. */
const MESSAGES_KEY = '__cotera_preview_messages';

export class RunAllPreviewsAction extends BaseAction<
  Inputs,
  { t: string; message?: string }[]
> {
  title = 'Run All Previews';
  group = COLUMN_ACTION_GROUP;
  icon = 'play';
  inputSchema = z.object({
    rows: z.number().min(1).max(300).optional(),
  });

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
      close: () => void;
    },
    private readonly db: Promise<CoteraDuckDB>,
    private readonly client: TenantedClient['atc']
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.hasTargetedModelOfType(LlmColumnModel) &&
      !context.targetedModelOfType(LlmColumnModel)!.hasErrors.snapshot() &&
      context.targetedModelOfType(LlmColumnModel)!.preview.snapshot() !== null
    );
  }

  async execute(
    { rows: numberOfRows }: { rows: number },
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(LlmColumnModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);
    const dataGridModel = context.targetedModelOfType(DataGridModel);

    Assert.assert(model !== null);
    Assert.assert(graphModel !== null);
    Assert.assert(dataGridModel !== null);

    const dataRel = dataGridModel.dataRel.snapshot();
    if (dataRel === null) {
      return err([{ t: 'error', message: 'No preview data available' }]);
    }

    const columnName = model.currentNameInGraph.snapshot();

    // Build the run config from the column model (mirrors AtcCellModel.llmConfig).
    const editorState = model.v2EditorState.snapshot();
    const docEditor = new DG.DocumentEditor.EditorState(editorState);
    const formState = model.formState.snapshot();
    const config = {
      t: 'llm' as const,
      version: 'v2' as const,
      outputTy: Ty.displayTy(docEditor.getOutputTy()),
      editorState,
      model: formState.model,
      schedule: formState.when,
      temperature: formState.temperature,
      maxOutputTokens: formState.maxOutputTokens,
      topP: 1,
      topK: 1,
      frequencyPenalty: 0,
      presencePenalty: 0,
    };

    const graph = graphModel.graph.snapshot();
    const expr = docEditor.getExpression(
      graph,
      graph.parser.forRel(model.datasetName)
    );
    if (expr.isErr()) {
      toast.error('Failed to parse expression');
      return err([{ t: 'error', message: 'Failed to parse expression' }]);
    }

    // Query the underlying preview data directly (the rendered grid is
    // virtualized, so only ~visible rows have AtcCellModels in scope). Fetch the
    // row data and the computed messages in a SINGLE query so each row's
    // stableId and messages always come from the same execution.
    const previewRel = dataRel.select(
      (t) => ({ ...t.star(), [MESSAGES_KEY]: expr.value }),
      { limit: numberOfRows }
    );

    const db = await this.db;
    const result = new DuckDBQueryResult(
      await db.query(previewRel),
      previewRel.attributes
    );
    const rows = result.toArray();
    const actualCount = rows.length;

    if (actualCount === 0) {
      toast.info('No rows to run previews for');
      return ok({});
    }

    const toastController = toast.loading(
      `Running previews for ${actualCount} rows...`
    );

    const identity = model.identity.snapshot();
    const relIdentity = identity.rel?.toString() ?? '';
    const colIdentity = identity.col?.toString() ?? '';

    const errors: { t: string; message?: string }[] = [];
    let successCount = 0;
    for (let i = 0; i < actualCount; i++) {
      const rawStableId = rows[i]?.['__cotera_stable_id'];
      const stableId =
        rawStableId !== null && rawStableId !== undefined
          ? String(rawStableId)
          : `${model.datasetName}-${columnName}-${i}`;
      const messages = result.row(i).column(MESSAGES_KEY).valueAt(0);

      try {
        await this.client.runColumnValue({
          config,
          relIdentity,
          colIdentity,
          stableId,
          messages,
          // The column-editor preview grid renders with isPreviewMode: true,
          // so completions are never persisted by id here.
          completionId: null,
        });
        successCount++;
      } catch (e) {
        errors.push({
          t: 'error',
          message: e instanceof Error ? e.message : 'Failed to run preview',
        });
      }
    }

    // Refresh any currently-rendered cells so visible rows re-poll immediately.
    // Off-screen rows pick up their result when scrolled into view.
    if (context.countInScopeModelsOfType(AtcCellModel) > 0) {
      const renderedCells = context
        .getInScopeModelsOfType(AtcCellModel)
        .filter(
          (x) =>
            x.relName === model.datasetName &&
            x.columnName === columnName &&
            x.rowIndex < actualCount
        );
      for (const cell of renderedCells) {
        cell.fetchKey.set(v4());
      }
    }

    if (errors.length > 0) {
      toastController.error(
        `Completed with errors: ${successCount} succeeded, ${errors.length} failed`
      );
    } else {
      toastController.success(`Successfully ran ${successCount} previews`);
    }

    return ok({});
  }

  shouldAsk(_context: ApplicableContext): boolean {
    return true;
  }

  ask(_: {}, _context: ExecuteContext): AskResponse<{}> {
    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <View
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.pop();

                  return ok({});
                }}
                onCancel={async () => {
                  this.modalManager.pop();
                  await onCancel();
                }}
              />
            );
          },
          onClose: async () => {
            this.modalManager.pop();
            await onCancel();
          },
        });
      },
    };
  }
}

const View: React.FC<{
  onRespond: (params: {
    rows: number;
  }) => Promise<ActionResult<{ t: string; message?: string }[]>> | void;
  onCancel: () => void;
}> = ({ onRespond, onCancel }) => {
  const [rows, setRows] = useState(100);
  return (
    <ModalPageView
      className="px-4 py-4"
      title="Run All Previews"
      subtitle="Run all previews for the selected column"
    >
      <div className="flex flex-col gap-4">
        How many rows do you want to run previews for?
        <Input
          type="number"
          min={1}
          max={300}
          value={rows}
          onChange={(e) => {
            setRows(Number(e.target.value));
          }}
        />
        <div className="w-full flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={async () => {
              await onRespond({ rows });
            }}
          >
            Run
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};
